// IELTSScoreCalculator.tsx
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { NextRouter, useRouter } from 'next/router';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import Header from './../../../components/Header';
import Footer from './../../../components/Footer';

import ScoreInput from './../../../components/score-calculator/ScoreInput';
import Results from './../../../components/score-calculator/Results';
import FAQ from '../../../components/score-calculator/FAQ';
import { ScoreValues } from './../../../components/score-calculator/Interfaces'
import { server } from '../../../config/index';

// Dynamically import ShareScore with SSR turned off
const ShareScore = dynamic(() => import('./../../../components/score-calculator/ShareScore'), {
    ssr: false,
});

interface IELTSScoreCalculatorProps {
    initialScores?: ScoreValues;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { query } = context;
    let initialScores: ScoreValues = {
        listening: 7,
        writing: 5.5,
        reading: 7,
        speaking: 6
    };

    if (query.l && query.r && query.w && query.s) {
        initialScores = {
            listening: parseFloat(query.l as string),
            reading: parseFloat(query.r as string),
            writing: parseFloat(query.w as string),
            speaking: parseFloat(query.s as string),
        };
    }

    return {
        props: {
            initialScores,
        },
    };
};


const IELTSScoreCalculator: React.FC<IELTSScoreCalculatorProps> = ({ initialScores }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const [scores, setScores] = useState<ScoreValues>(initialScores || {
        listening: 6,
        reading: 6,
        writing: 6,
        speaking: 6,
    });
    const [isLoading, setIsLoading] = useState(false);

    const router: NextRouter = useRouter();

    useEffect(() => {
        const newUrl = `/tools/ielts-score-calculator?l=${scores.listening}&r=${scores.reading}&w=${scores.writing}&s=${scores.speaking}`;
        router.replace(newUrl, undefined, { shallow: true });
    }, [scores]);

    const handleScoreChange = (name: keyof ScoreValues, newScore: number | string) => {
        setIsLoading(true);
        setScores((prevScores) => ({
            ...prevScores,
            [name]: newScore,
        }));
        setTimeout(() => {
            setIsLoading(false);
        }, 200);
    };

    const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
    const averageScore = totalScore / 4;
    const overallScore = Math.round(averageScore * 2) / 2;
    const lowestScore = Math.min(...Object.values(scores));

    return (
        <div className="flex flex-col min-h-screen justify-between">
            <Header />
            <Head>
                <title>Calculate IELTS Overall Band Online using 2023 Official IELTS Rules.</title>
                <meta name="description" content="2023 IELTS Score Calculator Online. Indication of study and immigration possibilities for each band. Works for both Academic and General IELTS." />
                <link rel="canonical" href="https://upscore.ai/ielts-score-calculator" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Calculate IELTS Overall Band Online using 2023 Official IELTS Rules." />
                <meta property="og:description" content="2023 IELTS Score Calculator Online. Indication of study and immigration possibilities for each band. Works for both Academic and General IELTS." />
                <meta property="og:image" content={`${server}/calc.jpg`} />
                <meta property="og:image:type" content="image/jpeg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:domain" content={`${server}`} />
                <meta name="twitter:title" content="Calculate IELTS Overall Band Online using 2023 Official IELTS Rules." />
                <meta name="twitter:description" content="2023 IELTS Score Calculator Online. Indication of study and immigration possibilities for each band. Works for both Academic and General IELTS." />
                <meta name="twitter:image" content={`${server}/calc.jpg`} />
            </Head>

            <div className="p-4 max-w-3xl m-auto">
                <h1 className="bg-blue-600 text-white text-center p-5 text-2xl font-semibold mb-4"> UpScore.AI: IELTS Score Calculator & Guidance</h1>

                <p className='prose whitespace-pre-wrap bg-slate-100 p-2 my-3 text-sm rounded'>
                    Here, you can calculate your IELTS score, based on official <a href="https://www.ielts.org/for-organisations/ielts-scoring-in-detail" target="_blank">rules</a>.<br />
                    Enter your scores for Listening, Reading, Writing, and Speaking.<br />
                    Scores should be between 0 and 9.0 with increments of 0.5.<br />
                </p>

                <ScoreInput scores={scores} onScoreChange={handleScoreChange} />

                {isLoading ? <div className="loading mt-5 text-lg">Processing...</div> : <Results overallScore={overallScore} averageScore={averageScore} lowestScore={lowestScore} />}

                <ShareScore scores={scores} />
                <FAQ />
            </div>

            <Footer />
        </div>
    );
};

export default IELTSScoreCalculator;